/** Filter bar for browse pages. Every filter lives in the URL so views are shareable. */
import { useSearchParams } from 'react-router'
import { useQuery } from '../lib/query'
import { useSession } from '../lib/session'
import type { VocabItem } from '../lib/types'

export function useFilter(name: string): [string, (v: string) => void] {
  const [params, setParams] = useSearchParams()
  const value = params.get(name) ?? ''
  const set = (v: string) => {
    const next = new URLSearchParams(params)
    if (v) next.set(name, v)
    else next.delete(name)
    next.delete('page')
    setParams(next, { replace: true })
  }
  return [value, set]
}

export function SelectFilter({
  name,
  label,
  options,
  allLabel,
}: {
  name: string
  label: string
  options: Array<{ value: string; label: string }>
  allLabel: string
}) {
  const [value, set] = useFilter(name)
  return (
    <label className="filter">
      <span className="filter-label">{label}</span>
      <select value={value} onChange={(e) => set(e.target.value)}>
        <option value="">{allLabel}</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  )
}

/** A select whose options come from a vocabulary, most-used first. */
export function VocabFilter({
  name,
  kind,
  label,
  allLabel,
  format = (v) => v,
}: {
  name: string
  kind: string
  label: string
  allLabel: string
  format?: (v: string) => string
}) {
  const { data } = useQuery<{ items: VocabItem[] }>(`/api/v1/vocab/${kind}`, { staleMs: 300_000 })
  const options = (data?.items ?? [])
    .filter((i) => i.count > 0)
    .sort((a, b) => b.count - a.count)
    .map((i) => ({ value: i.value, label: `${format(i.value)} (${i.count})` }))
  return <SelectFilter name={name} label={label} options={options} allLabel={allLabel} />
}

export function ToggleFilter({ name, label }: { name: string; label: string }) {
  const [value, set] = useFilter(name)
  return (
    <label className="filter filter-toggle">
      <input type="checkbox" checked={value === '1'} onChange={(e) => set(e.target.checked ? '1' : '')} />
      <span>{label}</span>
    </label>
  )
}

/**
 * Owned or not: shown to signed-in people, and on someone's shared collection (`?collector=`), where
 * it narrows by their collection instead.
 */
export function CollectionFilter() {
  const session = useSession()
  const [params] = useSearchParams()
  const collector = params.get('collector')
  if (!session.user && !collector) return null
  return (
    <SelectFilter
      name="owned"
      label="Collection"
      options={[
        { value: '1', label: collector ? 'Owned by them' : 'Owned' },
        { value: '0', label: collector ? 'Not owned by them' : 'Not owned' },
      ]}
      allLabel="Owned or not"
    />
  )
}
